import { APICONFIG } from '@config/api.config';
import { getDatabase } from '@rxdb/config/databaseConfig';
import { IQueryResponse } from '@services/api';
import { APIService } from '@services/api/common-services';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { usePermissionCheckForApiCallback } from '../../../authGuard';

export interface RigidUserObject {
	id: number;
	sfid: string;
	phone?: string | null;
	name: string;
	status?: string | null;
	createddate?: string | null;
	clubname?: string | null;
	contractorclass?: string | null;
	glue_consumption_per_month?: number | null;
	membershipno?: string | null;
	club_mapping?: string | null;
	variable_contractor_type_c?: string | null;
	venuestatus_c?: string | null;
	reason_for_approval_rejection_c?: string | null;
	mdicode?: string | null;
	is_a_ob?: boolean | null;
	contractorconsumption?: string | null;
	kyc_id_proof_verification?: string | null;
	is_zbc?: boolean | null;
	is_de_growing?: boolean | null;
	is_adopted_by?: boolean | null;
	is_disengaged_contractor?: boolean | null;
	priority?: number | null;
	totalbankingQTD?: number | null;
	pwg_banking_qtd?: number | null;
	visitstatus?: string | null;
	current_quarter_visitstatus?: boolean | null;
}

const mapRigidUser = (user: RigidUserObject): RigidUserObject => ({
	id: Number(user.id),
	sfid: user.sfid,
	phone: user.phone ?? null,
	name: user.name,
	status: user.status ?? null,
	createddate: user.createddate ?? null,
	clubname: user.clubname ?? null,
	contractorclass: user.contractorclass ?? null,
	glue_consumption_per_month: user.glue_consumption_per_month ?? null,
	membershipno: user.membershipno ?? null,
	club_mapping: user.club_mapping ?? null,
	variable_contractor_type_c: user.variable_contractor_type_c ?? null,
	venuestatus_c: user.venuestatus_c ?? null,
	reason_for_approval_rejection_c: user.reason_for_approval_rejection_c ?? null,
	mdicode: user.mdicode ?? null,
	is_a_ob: user.is_a_ob ?? null,
	contractorconsumption: user.contractorconsumption ?? null,
	kyc_id_proof_verification: user.kyc_id_proof_verification ?? null,
	is_zbc: user.is_zbc ?? null,
	is_de_growing: user.is_de_growing ?? null,
	is_adopted_by: user.is_adopted_by ?? null,
	is_disengaged_contractor: user.is_disengaged_contractor ?? null,
	priority: user.priority ?? null,
	totalbankingQTD: user.totalbankingQTD ?? null,
	pwg_banking_qtd: user.pwg_banking_qtd ?? null,
	visitstatus: user.visitstatus ?? null,
	current_quarter_visitstatus: user.current_quarter_visitstatus ?? null,
});

export const useRigidAllUserQuery = (): IQueryResponse<Array<RigidUserObject>> => {
	const [shouldFetch, setShouldFetch] = useState(true);
	const [isDbChecked, setIsDbChecked] = useState(false);
	const { permissionForApi } = usePermissionCheckForApiCallback({ componentName: 'AllRigidUser' });

	const fetchRigidUserData = async (): Promise<Array<RigidUserObject>> => {
		const response = await APIService.getData<Array<RigidUserObject>>(APICONFIG.ALL_RIGID_USER_API_RBDM, {
			limit: '0',
		});
		if (response) {
			const db = await getDatabase();
			const rigidUsers = await db.all_rigid.find().exec();
			const primaryId = rigidUsers.map((doc: { primary: string }) => doc.primary);
			await db.all_rigid.bulkRemove(primaryId);
			await db.all_rigid.bulkInsert(response.map(mapRigidUser));
		}
		return response;
	};

	const checkDb = async (): Promise<boolean> => {
		const db = await getDatabase();
		const result = await db.all_rigid.find().exec();
		return result.length > 0;
	};

	useEffect(() => {
		const checkDatabase = async (): Promise<void> => {
			const exists = await checkDb();
			setShouldFetch(!exists);
			setIsDbChecked(true);
		};
		checkDatabase();
	}, []);

	const { data, error, isLoading, isPending, refetch, isFetching } = useQuery({
		queryKey: ['Rigid User RBDM'],
		queryFn: fetchRigidUserData,
		enabled: permissionForApi && isDbChecked && shouldFetch,
		refetchOnWindowFocus: false,
		retry: false,
	});

	return {
		data,
		error,
		isLoading,
		isPending,
		refetch,
		isFetching,
	};
};
